import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Eye,
  Edit3,
  Trash2,
  Ban,
  Download,
  Mail,
} from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";

const DERECHOS = [
  {
    id: "acceso",
    icon: Eye,
    nombre: "Acceso",
    descripcion: "Ver qué datos personales tenemos sobre ti, cómo los obtuvimos, para qué los usamos y a quién se los hemos comunicado.",
    art: "Art. 12 letra a)",
  },
  {
    id: "rectificacion",
    icon: Edit3,
    nombre: "Rectificación",
    descripcion: "Corregir datos inexactos, incompletos o desactualizados.",
    art: "Art. 12 letra b)",
  },
  {
    id: "cancelacion",
    icon: Trash2,
    nombre: "Cancelación (supresión)",
    descripcion: "Eliminar tus datos cuando ya no sean necesarios o hayas retirado el consentimiento.",
    art: "Art. 12 letra c)",
  },
  {
    id: "oposicion",
    icon: Ban,
    nombre: "Oposición",
    descripcion: "Oponerte a tratamientos específicos (ej: decisiones automatizadas, perfilado, marketing).",
    art: "Art. 12 letra d)",
  },
  {
    id: "portabilidad",
    icon: Download,
    nombre: "Portabilidad",
    descripcion: "Recibir tus datos en formato estructurado (JSON/CSV) para llevarlos a otro proveedor.",
    art: "Art. 12 letra e)",
  },
];

const MAILTO_BASE = "privacidad@leydedatoschile.com";

function buildMailto({ derecho, nombre, email, detalle }) {
  const d = DERECHOS.find((x) => x.id === derecho);
  const subject = `Solicitud Derecho ARCO — ${d?.nombre || derecho}`;
  const body = [
    `Solicitante: ${nombre}`,
    `Email: ${email}`,
    `Derecho ejercido: ${d?.nombre || derecho} (${d?.art || ""})`,
    "",
    "Detalle de la solicitud:",
    detalle || "—",
    "",
    "Acreditación de identidad: confirmo que soy titular de los datos y que la información provista es veraz.",
    "Fecha: " + new Date().toISOString().slice(0, 10),
    "",
    "— Enviado vía /ejercer-derechos en leydedatoschile.com",
  ].join("\n");
  return `mailto:${MAILTO_BASE}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function EjercerDerechos() {
  const [form, setForm] = useState({
    derecho: "acceso",
    nombre: "",
    email: "",
    detalle: "",
  });
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.email || !form.nombre) return;
    const url = buildMailto(form);
    window.location.href = url;
    setSent(true);
  };

  return (
    <>
      <SEO
        title="Ejercer Derechos ARCO + Portabilidad — Ley 21.719"
        description="Solicita acceso, rectificación, cancelación, oposición o portabilidad de tus datos personales. Respondemos en máximo 30 días corridos según la Ley N° 21.719."
        path="/ejercer-derechos"
        keywords="derechos ARCO Chile, Ley 21.719, portabilidad de datos, acceso, rectificación, cancelación, oposición"
        schemas={[
          orgSchema,
          breadcrumbSchema([
            { name: "Inicio", path: "/" },
            { name: "Ejercer derechos", path: "/ejercer-derechos" },
          ]),
        ]}
      />
      <section className="px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E3A8A]/5 text-[#1E3A8A] text-xs font-semibold uppercase tracking-wide mb-6">
            <ShieldCheck className="w-3.5 h-3.5" />
            Ejercer Derechos · Ley 21.719
          </div>
          <h1
            data-testid="derechos-title"
            className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900"
          >
            Tus 5 derechos sobre los datos que tenemos de ti
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Como titular de datos personales, la Ley N° 21.719 te garantiza estos derechos —
            gratuitos y ejercitables en cualquier momento. Respondemos en máximo{" "}
            <strong className="text-slate-900">30 días corridos</strong>.
          </p>

          {/* Listado de derechos */}
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DERECHOS.map((d) => (
              <li
                key={d.id}
                className="flex items-start gap-3 p-4 rounded-2xl border border-slate-200 bg-white"
              >
                <div className="w-9 h-9 rounded-xl bg-[#1E3A8A]/5 text-[#1E3A8A] grid place-items-center shrink-0">
                  <d.icon className="w-4.5 h-4.5" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{d.nombre}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-0.5">{d.art}</div>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d.descripcion}</p>
                </div>
              </li>
            ))}
          </ul>

          {/* Formulario */}
          <div className="mt-12 p-7 rounded-2xl bg-slate-50 border border-slate-200">
            <h2 className="font-display text-2xl font-semibold text-slate-900">
              Enviá tu solicitud
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Te abriremos tu cliente de email con el mensaje pre-rellenado.
              Si preferís escribir directo: <a className="text-[#1E3A8A] underline" href={`mailto:${MAILTO_BASE}`}>{MAILTO_BASE}</a>
            </p>
            <form className="mt-6 space-y-4" onSubmit={onSubmit} data-testid="derechos-form">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 mb-1.5">
                  Qué derecho quieres ejercer
                </label>
                <select
                  value={form.derecho}
                  onChange={onChange("derecho")}
                  data-testid="derechos-select"
                  className="w-full h-11 px-3 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] outline-none"
                >
                  {DERECHOS.map((d) => (
                    <option key={d.id} value={d.id}>{d.nombre} — {d.art}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 mb-1.5">
                    Tu nombre completo
                  </label>
                  <input
                    type="text"
                    required
                    value={form.nombre}
                    onChange={onChange("nombre")}
                    data-testid="derechos-nombre"
                    className="w-full h-11 px-3 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 mb-1.5">
                    Tu email
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange("email")}
                    data-testid="derechos-email"
                    className="w-full h-11 px-3 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 mb-1.5">
                  Detalle (opcional)
                </label>
                <textarea
                  rows={4}
                  value={form.detalle}
                  onChange={onChange("detalle")}
                  placeholder="Cuéntanos más sobre tu solicitud. Por ejemplo: «Quiero el listado de los emails que enviamos a tu dirección» o «Pido que elimines mi cuenta y datos asociados»."
                  data-testid="derechos-detalle"
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:ring-2 focus:ring-[#1E3A8A]/20 focus:border-[#1E3A8A] outline-none resize-y"
                />
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Al enviar, confirmás que sos el titular de los datos. Te responderemos a la
                dirección de email que provistes en máximo 30 días corridos. Si rechazamos o no
                respondemos a tiempo, tienes derecho a reclamar ante la{" "}
                <strong className="text-slate-700">Agencia de Protección de Datos Personales (APDP)</strong>.
              </p>
              <button
                type="submit"
                data-testid="derechos-submit"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm font-semibold transition-colors"
              >
                <Mail className="w-4 h-4" />
                Abrir email con la solicitud
                <ArrowRight className="w-4 h-4" />
              </button>
              {sent && (
                <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  Email abierto. Si no se abrió, copia el destinatario manualmente: {MAILTO_BASE}
                </div>
              )}
            </form>
          </div>

          {/* Link a APDP */}
          <p className="mt-10 text-sm text-slate-600 leading-relaxed">
            Si no quedas conforme con nuestra respuesta, puedes reclamar ante la <strong>Agencia
            de Protección de Datos Personales</strong> una vez que esté operativa. Mientras
            tanto, puedes escribir a{" "}
            <a className="text-[#1E3A8A] underline" href="mailto:contacto@subrei.gob.cl">contacto@subrei.gob.cl</a>{" "}
            (Subsecretaría de Economía, autoridad transitoria mencionada en la Guía Práctica
            Min. Segpres).
          </p>

          <div className="mt-10 flex flex-wrap gap-3 text-sm">
            <Link to="/politica-privacidad" className="text-[#1E3A8A] underline hover:text-[#1E40AF]">
              Política de Privacidad
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/politica-cookies" className="text-[#1E3A8A] underline hover:text-[#1E40AF]">
              Política de Cookies
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
